import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Footer from '../Footer';
import Header from '../Header';
import SidebarWrapper from '../SidebarWrapper';
import { useLogin } from '../../hooks/useLogin';
import type { MenuFeatureItem, MenuItem } from '../types/types';
import { MenuItems } from 'src/components/menuItems';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { NotificationManager } from 'react-notifications';
interface MainLayoutProps {
  children: React.ReactNode;
}
interface RolePermission {
  roleID: number;
  roleName: string;
  menu: MenuFeatureItem[];
}

export default function MainLayout({ children }: MainLayoutProps): React.JSX.Element {
  const { handleAuthenticationFailure } = useLogin();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const storedIsCollapsed = localStorage.getItem('isCollapsed');
    return storedIsCollapsed !== null ? storedIsCollapsed === 'true' : false;
  });
  const navigate = useNavigate();
  const url = useLocation();

  const roleData = localStorage?.getItem('role') ?? "";
  const rolePermission = roleData !== "" ? JSON.parse(roleData) as RolePermission[] : [];

  const menuItems: MenuItem[] = MenuItems;

  useEffect(() => {
    const storedIsCollapsed = localStorage.getItem('isCollapsed');
    console.log(storedIsCollapsed);
    if (storedIsCollapsed !== null) {
      setIsCollapsed(storedIsCollapsed === 'true'); // Convert string to boolean
    } else {
      setIsCollapsed(false); // Default to false if not found in localStorage
    }
  }, []);


  useEffect(() => {
    localStorage.setItem('isCollapsed', isCollapsed.toString());
  }, [isCollapsed]);



  useEffect(() => {
    void handleAuthenticationFailure(); // Handle user session failure
  }, [handleAuthenticationFailure]);

  // Helper function to normalize URLs
  const normalizeLink = (url: string): string => {
    const segments = url.split('/').filter(Boolean); // Filter out empty strings
    return `/${segments.slice(0, 2).join('/')}/`; // Return first two segments
  };
  const clearAllNotifications = () : void=> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
    NotificationManager.listNotify.forEach((notification: any) =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      NotificationManager.remove(notification)
    );
  };
  useEffect(() => {
    clearAllNotifications();
    const menuLink = searchMenu();
    const menuPermission = checkRolePermission(menuLink);
    const urlPath = normalizeLink(url.pathname);
    const isManageUsersEditPath = /^\/settings\/manage-users\/edit\/\d+$/.test(url.pathname);
    // If no valid menuLink or no permission, redirect to 404
    if (menuLink === "" || !menuPermission) {
      // Special cases: allow certain pages without permission checks
      if (
        urlPath !== '/manage-jobs/new-request/' &&
        urlPath !== '/detailedsavedreport/' &&
        !isManageUsersEditPath
      ) {
        navigate('/404');
      }
    }
  }, [url]);

  // Find the menuKey for the current path
  const searchMenu = (): string => {
    const urlPath = normalizeLink(url.pathname);
    let menuKey = "";
    
    for (const item of menuItems) {
      const normalizedMenuKey = normalizeLink(item.link);

      // Match exact links for menus without submenus
      if (!item.hasSubmenu && normalizedMenuKey === urlPath) {
        menuKey = item.menuKey ?? "";
        break;
      }

      // Match links within submenu if exists
      if (item.subMenu) {
        for (const feature of item.subMenu) {
          const normalizedFeatureKey = normalizeLink(feature.link);
          if (normalizedFeatureKey === urlPath) {
            menuKey = feature.menuFeatureKey ?? "";
            break;
          }
        }
      }
    }

    return menuKey;
  };

  // Check if the user has permission to access the menuKey
  const checkRolePermission = (foundKey: string): boolean => {
    if (rolePermission.length === 0) return false;

    const permissionData: MenuFeatureItem[] = rolePermission[0].menu;
    for (const menuItem of permissionData) {
      if (menuItem.menuKey === foundKey) {
        return true; // Found permission
      }
      
      // Check if user has permission for submenu
      if (menuItem.menuFeatures.length > 0) {
        for (const feature of menuItem.menuFeatures) {
          if (feature.menuFeatureKey === foundKey && feature.isSubMenu) {
            return true;  // Found submenu permission
          }
        }
      }
    }

    return false; // No matching key found
  };

  return (
    <>
      <SidebarWrapper isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
      <div className="flex-1 main-container h-[100%]">
        <Header />
        <div className="px-10 py-5 min-h-[87vh] max-h-[100%]">
          {React.Children.map(children, (child) =>
            React.isValidElement(child)
              ? React.cloneElement(child as React.ReactElement<{ isCollapsed?: boolean }>, { isCollapsed })
              : child
          )}
        </div>
        <Footer />
      </div>
    </>
  );
}
